import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import "./AccountingPage.css";
import SettlementEvidence from "./SettlementEvidence";
import BankStatements from './BankStatements';
import PaymentDebitEvidence from './PaymentDebitEvidence';

export default function TenderClearing() {
  const { accessToken } = useAuth();
  const [companies, setCompanies] = useState([]),
    [company, setCompany] = useState(""),
    [date, setDate] = useState(""),
    [registration, setRegistration] = useState(""),
    [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    let active = true;
    generation.current++;
    setData(null);
    setCompanies([]);
    setCompany("");
    setRegistration("");
    setError("");
    setBusy(false);
    apiRequest("/accounting/companies", { accessToken })
      .then((r) => {
        if (active) {
          setCompanies(r);
          setCompany(r[0]?.id || "");
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
      generation.current++;
    };
  }, [accessToken]);
  function change(setter, value) {
    generation.current++;
    setter(value);
    setData(null);
    setError("");
    setBusy(false);
  }
  async function load(offset = 0) {
    const g = ++generation.current;
    setBusy(true);
    setError("");
    setData(null);
    try {
      const q = new URLSearchParams({ asOf: date, offset: String(offset) });
      if (registration) q.set("registrationId", registration);
      const result = await apiRequest(
        `/accounting/${company}/tender-clearing?${q}`,
        { accessToken },
      );
      if (g === generation.current) setData(result);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) setBusy(false);
    }
  }
  const field =
    "mt-1 min-h-11 w-full rounded-xl border border-slate-200 bg-white p-3";
  return (
    <main className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Card & UPI clearing review</h1>
        <p className="mt-2 text-slate-600">
          Review POS recognition separately from bank settlement. Cashfree, card
          terminals and direct-bank UPI are not automatically matched.
        </p>
      </header>
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
        Bank verification is not automatic. Reviewed settlement vouchers can be recorded in the workspace below; refunds are not executed here. Matching ledger totals does not prove money
        reached the bank.
      </div>
      <form
        className="grid gap-4 rounded-2xl border bg-white p-5 md:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <label>
          Accounting company
          <select
            className={field}
            value={company}
            onChange={(e) => {
              change(setCompany, e.target.value);
              setRegistration("");
            }}
          >
            <option value="">Select company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          As of date (India)
          <input
            className={field}
            type="date"
            required
            value={date}
            onChange={(e) => change(setDate, e.target.value)}
          />
        </label>
        <label>
          GST registration
          <select
            className={field}
            value={registration}
            onChange={(e) => change(setRegistration, e.target.value)}
          >
            <option value="">All registrations</option>
            {(data?.registrations || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.gstin}
              </option>
            ))}
            {registration && !data && (
              <option value={registration}>Selected registration</option>
            )}
          </select>
        </label>
        <button
          className="self-end rounded-xl bg-primary-700 px-4 py-3 font-semibold text-white disabled:opacity-40"
          disabled={!company || !date || busy}
        >
          {busy ? "Loading…" : "Review clearing"}
        </button>
      </form>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {data && (
        <>
          <p role="status" className="rounded-xl bg-slate-100 p-4">
            {data.needsReview
              ? "Recognition / other-entry review required"
              : "Posted recognition agrees"}{" "}
            · Bank settlements NOT VERIFIED
          </p>
          <section className="overflow-x-auto rounded-2xl border bg-white p-5">
            <h2 className="mb-3 text-lg font-semibold">
              Clearing ledger control — all history through {data.asOf}
            </h2>
            <table className="w-full text-left">
              <thead>
                <tr>
                  {[
                    "Ledger / GST",
                    "Expected POS recognition ₹",
                    "Linked GL net ₹",
                    "Recognition difference ₹",
                    "Net posted settlement credits ₹",
                    "Other debits / credits ₹",
                    "Recorded GL net ₹",
                    "Review",
                  ].map((h) => (
                    <th key={h} className="p-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.groups.map((r) => (
                  <tr
                    key={r.ledgerId + ":" + r.registrationId}
                    className="border-t"
                  >
                    <td className="p-3">
                      {r.ledgerCode} · {r.ledgerName}
                      <small className="block text-slate-500">
                        {r.gstin || "No GST registration"}
                      </small>
                    </td>
                    <td>{r.expectedRecognition}</td>
                    <td>{r.linkedNet}</td>
                    <td>{r.recognitionDifference}</td>
                    <td>{r.settlementCredit || '0.00'}<small className="block">Unsettled recognition: {r.unsettledRecognition || r.expectedRecognition}</small></td>
                    <td>
                      {r.otherDebit} / {r.otherCredit}
                      <small className="block">
                        {r.otherLineCount} unexplained entries
                      </small>
                    </td>
                    <td>{r.recordedNet}</td>
                    <td>
                      {r.needsReview ? "Review required" : "Recognition agrees"}
                      <small className="block">
                        Bank not verified
                        {r.reversedSourceCount > 0
                          ? " · Reversed sources require review"
                          : ""}
                      </small>
                    </td>
                  </tr>
                ))}
                {!data.groups.length && (
                  <tr>
                    <td colSpan={7} className="p-4">
                      No configured or source-linked clearing ledgers.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
          <section className="overflow-x-auto rounded-2xl border bg-white p-5">
            <h2 className="mb-3 text-lg font-semibold">
              Posted POS tender history
            </h2>
            <table className="w-full text-left">
              <thead>
                <tr>
                  {[
                    "Date / Receipt",
                    "Voucher",
                    "Tender",
                    "Amount ₹",
                    "Recorded references — unverified",
                  ].map((h) => (
                    <th className="p-3" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr className="border-t" key={e.sourceId + ":" + e.method}>
                    <td className="p-3">
                      {e.date}
                      <span className="block">{e.receiptNumber}</span>
                    </td>
                    <td>
                      {e.voucherNumber}
                      {e.reversed && (
                        <small className="block">Reversed — review</small>
                      )}
                    </td>
                    <td>{e.method}</td>
                    <td>{e.amount}</td>
                    <td>
                      {e.recordedPayments.map((p, i) => (
                        <span className="block" key={p.paymentId || i}>
                          {p.reference || "Missing reference"} · ₹{p.amount}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
                {!data.events.length && (
                  <tr>
                    <td colSpan={5} className="p-4">
                      No posted card/UPI tender sources through this date.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="mt-4 flex items-center gap-4">
              <button
                disabled={busy || data.offset === 0}
                onClick={() => load(Math.max(0, data.offset - data.limit))}
              >
                Previous
              </button>
              <span>
                {data.totalCount ? data.offset + 1 : 0}–
                {Math.min(data.offset + data.limit, data.totalCount)} of{" "}
                {data.totalCount}
              </span>
              <button
                disabled={busy || data.offset + data.limit >= data.totalCount}
                onClick={() => load(data.offset + data.limit)}
              >
                Next
              </button>
            </div>
          </section>
          <p className="text-sm text-slate-500">{data.explanation}</p>
        </>
      )}
      {company && (
        <SettlementEvidence
          key={company + ":" + accessToken}
          companyId={company}
        />
      )}
      {company && <BankStatements key={'bank:'+company+':'+accessToken} companyId={company}/>}
      {company && <PaymentDebitEvidence key={'debits:'+company+':'+accessToken} companyId={company}/>}
    </main>
  );
}
